import React, { FC } from 'react'
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { MedicationInsertButton } from '@/apps/legacy/components/molecules/MedicationInsertButton'
import { insertAtCursor } from '@/shared/utils/textFieldHelper'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useEditableItem } from '@/shared/hooks/useEditableItem'

interface EditableSOAPItemProps {
  content: string
  medications: string[]
  isEditMode: boolean // 編集モードかどうか
  isEditing: boolean // この項目が編集中かどうか
  onStartEdit: () => void
  onSave: (newContent: string) => Promise<void>
  onCancel: () => void
  disabled?: boolean
}

export const EditableSOAPItem: FC<EditableSOAPItemProps> = ({
  content,
  medications,
  isEditMode,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  disabled = false,
}) => {
  const {
    editValue,
    setEditValue,
    isSaving,
    inputRef,
    handleSave: hookHandleSave,
    handleCancel: hookHandleCancel,
    handleKeyDown: hookHandleKeyDown,
  } = useEditableItem({
    initialContent: content,
    isEditing,
  })

  const handleMedicationInsert = (text: string) => {
    insertAtCursor(inputRef, editValue, setEditValue, text)
  }

  const handleSave = async () => {
    try {
      await hookHandleSave(content, onSave)
      onCancel()
    } catch {
      // エラーハンドリングはフック内で処理済み
    }
  }

  const handleCancel = () => {
    hookHandleCancel(content)
    onCancel()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    hookHandleKeyDown(e, content, onSave, onCancel)
  }

  // 編集中の表示
  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          backgroundColor: 'action.hover',
          borderRadius: 1,
          p: 0.5,
          width: '100%',
        }}
      >
        <TextField
          inputRef={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          size="small"
          variant="outlined"
          multiline
          minRows={1}
          maxRows={10}
          disabled={isSaving}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            },
            '& .MuiInputBase-input': {
              minHeight: '1.2em',
            },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <MedicationInsertButton
            medications={medications}
            onInsert={handleMedicationInsert}
            buttonSize="small"
            disabled={isSaving}
            tooltip="医薬品を挿入"
          />

          {isSaving ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Tooltip title="保存 (Enter)">
                <Box component="span" sx={{ display: 'inline-block' }}>
                  <IconButton
                    onClick={handleSave}
                    size="small"
                    disabled={isSaving}
                    sx={{
                      color: colorTokens.status.success,
                      '&:hover': {
                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                      },
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>

              <Tooltip title="キャンセル (Esc)">
                <Box component="span" sx={{ display: 'inline-block' }}>
                  <IconButton
                    onClick={handleCancel}
                    size="small"
                    disabled={isSaving}
                    sx={{
                      color: colorTokens.text.secondary,
                      '&:hover': {
                        backgroundColor: 'rgba(117, 117, 117, 0.08)',
                        color: colorTokens.grey[700],
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    )
  }

  // 通常表示（編集モード時は編集ボタン付き）
  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          position: 'relative',
        },
        isEditMode
          ? {
              '&:hover .edit-button': {
                opacity: 1,
              },
            }
          : {
              '&:hover .edit-button': {
                opacity: 0,
              },
            },
      ]}
    >
      <Box sx={{ flex: 1 }}>{content}</Box>
      {isEditMode && !disabled && (
        <IconButton
          className="edit-button"
          onClick={onStartEdit}
          size="small"
          sx={{
            opacity: 0,
            transition: 'opacity 0.2s',
            color: colorTokens.text.secondary,
            '&:hover': {
              color: colorTokens.blue[600],
              opacity: 1,
            },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  )
}
